import { BBoxPixels, SegmentationData, Annotation, HiddenReason } from "@/types/annotations";
import { decodeCompressedRLEToMask } from "@/utils/rleUtils";
import {
  evaluateOverlapSuppression,
  resolveDetectionClassLabel,
  type ContainmentParams,
  type OverlapDetection,
} from "@shared/containmentOverlapFilter";
import { getMeasurementAxisValue, parseMeasurementEndpointName } from "@shared/types/measurementDescriptors";
import {
  getAnnotationClassColor,
  resolveAnnotationClassName,
  type AnnotationClassRegistry,
} from "@shared/annotationClassColors";
import { MASK_COLORS } from "@shared/constants/annotationMaskColors";
import type { Point } from "../types";
import { isAcceptedOutsideBoundaryAnnotation } from "../outsideBoundaryAnnotations";

const MODEL_OUTLINE_COLOR = "#38bdf8";

/**
 * An axis drawn in the editor/viewer with no stored value (no depth, depth not
 * loaded yet, or depth that could not value it) is dashed and grey, so it never
 * reads as a measurement. Viewer-only: exports keep the axis colour.
 */
export const UNMEASURED_AXIS_STROKE = "#9ca3af";
/** Screen-pixel dash pattern; divide by the zoom scale at the draw site. */
export const UNMEASURED_AXIS_DASH = [10, 8];

export const isMeasurementAxisValued = (measurements: Annotation["measurements"], axisKey: string): boolean => {
  const value = getMeasurementAxisValue(measurements, axisKey);
  return typeof value === "number" && Number.isFinite(value) && value > 0;
};
const MANUAL_OUTLINE_COLOR = "#f97316";

/**
 * Parse a hex color string to RGB components.
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const h = hex.replace("#", "");
  return {
    r: parseInt(h.substring(0, 2), 16),
    g: parseInt(h.substring(2, 4), 16),
    b: parseInt(h.substring(4, 6), 16),
  };
}

export const maskToCanvas = (
  bbox?: BBoxPixels,
  segmentationData?: SegmentationData,
  color: string = "#00FFFF",
  alpha: number = 0.4
) => {
  const mask = decodeCompressedRLEToMask(segmentationData?.compressedRLE);
  const { x, y, height: bboxHeight, width: bboxWidth } = bbox;

  const height = mask.length;
  const width = mask[0].length;

  // Create temporary canvas with original mask dimensions
  const tempCanvas = document.createElement("canvas");
  tempCanvas.width = width;
  tempCanvas.height = height;

  const tempCtx = tempCanvas.getContext("2d");
  const imageData = tempCtx.createImageData(width, height);
  const { r, g, b } = hexToRgb(color);
  const alphaValue = Math.round(alpha * 255);

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4;
      imageData.data[i] = r;
      imageData.data[i + 1] = g;
      imageData.data[i + 2] = b;
      imageData.data[i + 3] = mask[y][x] ? alphaValue : 0;
    }
  }

  tempCtx.putImageData(imageData, 0, 0);

  // Create final canvas with bbox dimensions and upscale the mask
  const canvas = document.createElement("canvas");
  canvas.width = bboxWidth;
  canvas.height = bboxHeight;

  const ctx = canvas.getContext("2d");
  ctx.drawImage(tempCanvas, 0, 0, width, height, 0, 0, bboxWidth, bboxHeight);

  return { canvas, x, y };
};

export const isManualBBoxAnnotation = (annotation: Annotation): boolean =>
  Boolean(annotation.bbox && annotation.userAdjustment?.manuallyCreated && "humanAnnotated" in annotation.source);

export const resolveSegmentationAnnotationColors = (
  annotation: Annotation,
  index: number,
  registry: AnnotationClassRegistry,
  configuredClassNames: readonly string[] = []
): { color: string; outlineColor: string } => {
  if (registry.isMultiClass) {
    const classColor = getAnnotationClassColor(registry, resolveAnnotationClassName(annotation, configuredClassNames));
    return { color: classColor, outlineColor: classColor };
  }

  return {
    color: MASK_COLORS[index % MASK_COLORS.length],
    outlineColor: isManualBBoxAnnotation(annotation) ? MANUAL_OUTLINE_COLOR : MODEL_OUTLINE_COLOR,
  };
};

export const isAnnotationEffectivelyVisible = (
  visibility: { isVisible: boolean; isBelowThreshold: boolean } | undefined
): boolean => Boolean(visibility && (visibility.isVisible || visibility.isBelowThreshold));

/**
 * Segmentation/image-measurement annotations are rendered in the same display-oriented
 * coordinate space that their bboxes, masks, and keypoints are stored in. The boundary
 * must stay in that same space; applying the image orientation here rotates only the
 * boundary while leaving the rest of the overlay unchanged.
 */
export const getSegmentationDisplayBoundaryPixels = (measureBoundaryPixels?: Point[]): Point[] =>
  measureBoundaryPixels ?? [];

/**
 * Compute Intersection over Union between two bounding boxes
 */
export function computeIoU(a: BBoxPixels, b: BBoxPixels): number {
  const x1 = Math.max(a.x, b.x);
  const y1 = Math.max(a.y, b.y);
  const x2 = Math.min(a.x + a.width, b.x + b.width);
  const y2 = Math.min(a.y + a.height, b.y + b.height);
  const intersection = Math.max(0, x2 - x1) * Math.max(0, y2 - y1);
  if (intersection === 0) return 0;
  const unionArea = a.width * a.height + b.width * b.height - intersection;
  return intersection / unionArea;
}

/**
 * A bounding box paired with its source list index, confidence, and precomputed center.
 * The editor precomputes these once per annotation list so per-event hit-testing is a
 * tight, allocation-free scan.
 */
export interface BBoxCandidate {
  index: number;
  bbox: BBoxPixels;
  confidence: number;
  centerX: number;
  centerY: number;
}

/**
 * How far below a row's anchor a box may sit and still count as part of that row, as a fraction of
 * the median box height. Exported so it stays tunable: dense, near-random captures group less
 * cleanly than planted rows.
 */
export const ROW_TOLERANCE_RATIO = 0.6;

/** Median of a numeric list; 0 for an empty list. */
const median = (values: number[]): number => {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[middle - 1] + sorted[middle]) / 2 : sorted[middle];
};

/**
 * Sort candidates into human reading order — top-to-bottom by row, left-to-right within a row — so
 * stepping through objects sweeps the image predictably instead of following arbitrary model output
 * order.
 *
 * Rows are grown from a FIXED anchor (the first box of the row) rather than a running mean: a
 * running mean keeps absorbing each next box and chains diagonally across the whole image, which
 * collapses a grid into one row. The cost of a fixed anchor is that a gently descending row splits
 * in two, which is the far less confusing failure.
 *
 * Ties break on the source list index so the order — and therefore the "Object N of M" counter —
 * is stable across re-renders.
 */
export const orderCandidatesForReview = (candidates: BBoxCandidate[]): BBoxCandidate[] => {
  if (candidates.length < 2) return [...candidates];

  const rowBand = ROW_TOLERANCE_RATIO * median(candidates.map((candidate) => candidate.bbox.height));
  const byReadingPosition = [...candidates].sort(
    (a, b) => a.centerY - b.centerY || a.centerX - b.centerX || a.index - b.index
  );

  const ordered: BBoxCandidate[] = [];
  let row: BBoxCandidate[] = [];
  let anchorY = byReadingPosition[0].centerY;

  const flushRow = () => {
    row.sort((a, b) => a.centerX - b.centerX || a.index - b.index);
    ordered.push(...row);
    row = [];
  };

  for (const candidate of byReadingPosition) {
    if (row.length > 0 && candidate.centerY - anchorY > rowBand) {
      flushRow();
      anchorY = candidate.centerY;
    }
    row.push(candidate);
  }
  flushRow();

  return ordered;
};

/** Below this in EITHER dimension, a draft box was a click rather than a drawn box. */
const MIN_DRAW_SIZE = 8;

/**
 * Whether a finished draft box should become a manual annotation.
 *
 * A press inside an existing box may still be the start of a new box drawn OVER it, so
 * mousedown always begins a draft and mouseup decides: a negligible drag is a click
 * (select), anything larger is a draw. Model output routinely contains large
 * false-positive boxes, so refusing to start a draw inside one would leave nowhere to draw.
 */
export const isDraftBoxDrawable = (box: BBoxPixels): boolean =>
  box.width >= MIN_DRAW_SIZE && box.height >= MIN_DRAW_SIZE;

/** Inclusive point-in-rectangle test. */
const pointInBBox = (point: Point, bbox: BBoxPixels): boolean =>
  point.x >= bbox.x && point.x <= bbox.x + bbox.width && point.y >= bbox.y && point.y <= bbox.y + bbox.height;

/** IoU threshold above which two boxes are treated as near-duplicates. Mirrors the iOS editor. */
const DUPLICATE_IOU_THRESHOLD = 0.9;

/**
 * Find which annotation a double-click / hover should target, mirroring the iOS
 * captured-image editor (CapturedImageView.handleImageViewDoubleTap):
 *   1. Consider only boxes whose bbox CONTAINS the point.
 *   2. If none contain it, return -1.
 *   3. Pick the containing box whose center is closest to the point.
 *   4. Among containing boxes that are near-duplicates of that closest box
 *      (IoU >= 0.9), return the one with the highest confidence.
 * This lets a small box be selected even when a large box is drawn on top of it,
 * because the small box's center is nearer to a click aimed at it. Ties resolve to
 * the first candidate in list order. Returns the annotation list index, or -1.
 */
export function findToggleTargetIndex(point: Point, candidates: BBoxCandidate[]): number {
  let closest: BBoxCandidate | null = null;
  let closestDistanceSq = Infinity;
  for (const candidate of candidates) {
    if (!pointInBBox(point, candidate.bbox)) continue;
    const dx = candidate.centerX - point.x;
    const dy = candidate.centerY - point.y;
    const distanceSq = dx * dx + dy * dy;
    if (distanceSq < closestDistanceSq) {
      closestDistanceSq = distanceSq;
      closest = candidate;
    }
  }
  if (!closest) return -1;

  let best = closest;
  for (const candidate of candidates) {
    if (candidate === closest) continue;
    if (!pointInBBox(point, candidate.bbox)) continue;
    if (computeIoU(closest.bbox, candidate.bbox) < DUPLICATE_IOU_THRESHOLD) continue;
    if (candidate.confidence > best.confidence) best = candidate;
  }
  return best.index;
}

/**
 * Hidden reasons owned and recomputed by the UI filter sliders. Every other
 * reason (edgeCropped, outsideBoundary, etc.) is set elsewhere (e.g. the
 * deferred-analysis backend) and must be preserved when filters are reapplied.
 */
const FILTER_MANAGED_HIDDEN_REASONS: ReadonlySet<HiddenReason> = new Set([
  "confidenceBelowThreshold",
  "sizeTooLarge",
  "distanceTooFar",
  "pixelSizeTooLarge",
  "overlapTooHigh",
]);

const FILTER_KEY_BY_HIDDEN_REASON: Partial<Record<HiddenReason, string>> = {
  confidenceBelowThreshold: "sensitivity",
  sizeTooLarge: "maxLength",
  distanceTooFar: "cameraDistance",
  pixelSizeTooLarge: "bboxLength",
  overlapTooHigh: "maxOverlapIou",
};

/** A system-owned hide (edgeCropped, outsideBoundary, unknown future reasons):
 *  neither filter-managed nor the caller-handled userToggledOff. */
const isPreservedSystemReason = (reason: HiddenReason): boolean =>
  reason !== "userToggledOff" && !FILTER_MANAGED_HIDDEN_REASONS.has(reason);

/**
 * Combine an annotation's freshly computed filter hidden-reasons with the
 * non-filter ("system") reasons it already carried, so reapplying or resetting
 * filters never unhides a server-hidden detection (edgeCropped / outsideBoundary).
 * `userToggledOff` is handled by the caller and intentionally not preserved here.
 */
export function mergeHiddenReasons(
  existingReasons: HiddenReason[] | undefined,
  filterReasons: HiddenReason[],
  activeFilters?: ReadonlySet<string>
): HiddenReason[] {
  const preserved = (existingReasons ?? []).filter((reason) => {
    if (isPreservedSystemReason(reason)) return true;
    if (!activeFilters || reason === "userToggledOff") return false;
    const owningFilter = FILTER_KEY_BY_HIDDEN_REASON[reason];
    return Boolean(owningFilter && !activeFilters.has(owningFilter));
  });
  return [...new Set([...preserved, ...filterReasons])];
}

/**
 * Resolve which annotation a double-click should toggle, preferring the box the
 * hover highlight indicates. Rendered (visible / below-threshold) boxes are
 * considered first so the toggle always matches what the user sees highlighted;
 * hidden boxes are only consulted when no rendered box contains the click, which
 * preserves the ability to re-enable a hidden box by clicking where it sits.
 * Returns the annotation list index, or -1.
 */
export function resolveDoubleClickTargetIndex(
  point: Point,
  renderedCandidates: BBoxCandidate[],
  allCandidates: BBoxCandidate[]
): number {
  const rendered = findToggleTargetIndex(point, renderedCandidates);
  if (rendered !== -1) return rendered;
  return findToggleTargetIndex(point, allCandidates);
}

/** True when the user explicitly hid this annotation (double-click / delete).
 *  Web equivalent of iOS `userWantsSkipped == true`. */
export const hasUserToggledOff = (annotation: Annotation): boolean =>
  annotation.userVisibility?.isVisible === false &&
  Boolean(annotation.userVisibility.hiddenReasons?.includes("userToggledOff"));

export type SegmentationFilterThresholds = {
  confidenceThreshold: number;
  maxLengthM: number | null;
  maxCameraDistanceM: number | null;
  maxBboxLengthPx: number | null;
  maxOverlapIou: number;
};

export function resolveEffectiveFilterThresholds(
  displayThresholds: SegmentationFilterThresholds,
  activatedFilters: ReadonlySet<string>
): SegmentationFilterThresholds {
  return {
    confidenceThreshold: activatedFilters.has("sensitivity") ? displayThresholds.confidenceThreshold : 0,
    maxLengthM: activatedFilters.has("maxLength") ? displayThresholds.maxLengthM : null,
    maxCameraDistanceM: activatedFilters.has("cameraDistance") ? displayThresholds.maxCameraDistanceM : null,
    maxBboxLengthPx: activatedFilters.has("bboxLength") ? displayThresholds.maxBboxLengthPx : null,
    maxOverlapIou: activatedFilters.has("maxOverlapIou") ? displayThresholds.maxOverlapIou : 1,
  };
}

export function computeMeasurementLengthPixelDistances(annotations: Annotation[]): (number | undefined)[] {
  return annotations.map((annotation) => {
    let start: { x: number; y: number } | undefined;
    let end: { x: number; y: number } | undefined;
    for (const keypoint of annotation.keypoints ?? []) {
      const parsed = parseMeasurementEndpointName(keypoint.name);
      if (!parsed || parsed.key !== "length") continue;
      if (parsed.endpoint === "start" && !start) start = keypoint;
      if (parsed.endpoint === "end" && !end) end = keypoint;
    }
    if (!start || !end) return undefined;
    return Math.hypot(end.x - start.x, end.y - start.y);
  });
}

export function applyComputedFilterVisibility(
  annotations: Annotation[],
  reasonsByIndex: HiddenReason[][],
  activeFilters?: ReadonlySet<string>
): Annotation[] {
  return annotations.map((annotation, index) => {
    const isAcceptedOutside = isAcceptedOutsideBoundaryAnnotation(annotation);
    if (
      isManualBBoxAnnotation(annotation) ||
      (annotation.userVisibility?.manualOverride === true && !isAcceptedOutside)
    ) {
      return annotation;
    }
    // iOS collector deletions predate manualOverride and carry only this reason.
    // A web filter pass must never reinterpret that explicit field decision.
    if (annotation.userVisibility?.hiddenReasons?.includes("userToggledOff")) return annotation;

    if (isAcceptedOutside) {
      // Acceptance is a human override of scalar model filters, matching the
      // keypoint editor. Overlap remains authoritative so accepting the outside
      // set cannot reintroduce a duplicate that NMS/containment suppressed.
      const preservedBlockingReasons = (annotation.userVisibility?.hiddenReasons ?? []).filter(
        (reason) => isPreservedSystemReason(reason) && reason !== "outsideBoundary"
      );
      const computedBlockingReasons = getBlockingFilterHiddenReasons(annotation, reasonsByIndex[index] ?? []);
      const blockingReasons = [...new Set([...preservedBlockingReasons, ...computedBlockingReasons])];
      const nextIsVisible = blockingReasons.length === 0;
      const nextHiddenReasons: HiddenReason[] = ["outsideBoundary", ...blockingReasons];
      const currentVisibility = annotation.userVisibility;
      if (
        currentVisibility?.isVisible === nextIsVisible &&
        currentVisibility.manualOverride === true &&
        currentVisibility.hiddenReasons?.length === nextHiddenReasons.length &&
        currentVisibility.hiddenReasons.every((reason, reasonIndex) => reason === nextHiddenReasons[reasonIndex])
      ) {
        return annotation;
      }
      return {
        ...annotation,
        userVisibility: {
          isVisible: nextIsVisible,
          manualOverride: true,
          hiddenReasons: nextHiddenReasons,
        },
      };
    }

    const hiddenReasons = mergeHiddenReasons(
      annotation.userVisibility?.hiddenReasons,
      reasonsByIndex[index] ?? [],
      activeFilters
    );
    const isVisible = hiddenReasons.length === 0;
    return {
      ...annotation,
      userVisibility: {
        isVisible,
        ...(isVisible ? {} : { hiddenReasons }),
      },
    };
  });
}

export function getBlockingFilterHiddenReasons(
  annotation: Pick<Annotation, "userVisibility">,
  computedReasons: HiddenReason[]
): HiddenReason[] {
  return isAcceptedOutsideBoundaryAnnotation(annotation)
    ? computedReasons.filter((reason) => reason === "overlapTooHigh")
    : computedReasons;
}

/** Pass-1 slider reasons for one annotation (everything except the overlap
 *  stage). Push order is part of the persisted contract — don't reorder. */
function computePassOneReasons(
  annotation: Annotation,
  bboxKeypointDistance: number | undefined,
  thresholds: SegmentationFilterThresholds
): HiddenReason[] {
  const reasons: HiddenReason[] = [];
  if (annotation.confidence !== undefined && annotation.confidence < thresholds.confidenceThreshold) {
    reasons.push("confidenceBelowThreshold");
  }
  if (
    thresholds.maxLengthM !== null &&
    annotation.measurements?.lengthM !== undefined &&
    annotation.measurements.lengthM > thresholds.maxLengthM
  ) {
    reasons.push("sizeTooLarge");
  }
  if (
    thresholds.maxCameraDistanceM !== null &&
    annotation.measurements?.distanceFromCamM !== undefined &&
    annotation.measurements.distanceFromCamM > thresholds.maxCameraDistanceM
  ) {
    reasons.push("distanceTooFar");
  }
  if (
    thresholds.maxBboxLengthPx !== null &&
    bboxKeypointDistance !== undefined &&
    bboxKeypointDistance > thresholds.maxBboxLengthPx
  ) {
    reasons.push("pixelSizeTooLarge");
  }
  return reasons;
}

const hasActivePreservedSystemReason = (annotation: Annotation): boolean => {
  const acceptedOutside = isAcceptedOutsideBoundaryAnnotation(annotation);
  return (annotation.userVisibility?.hiddenReasons ?? []).some(
    (reason) => isPreservedSystemReason(reason) && !(acceptedOutside && reason === "outsideBoundary")
  );
};

/**
 * Compute every annotation's filter-managed hidden reasons in one pass.
 * Manual annotations always get [].
 *
 * Without `containment` the overlap stage is the existing plain-IoU
 * `applyNMS` — byte-identical to the historical per-annotation path.
 *
 * With `containment` (traits that opted in, params resolved by
 * `resolveContainmentParams`) the overlap stage mirrors iOS exactly
 * (CapturedImageView Pass 3 + ContainmentOverlapFilter):
 * candidates are bbox+confidence detections (mask not required) with no active
 * preserved system hide, user toggle, or manual-box source. Normally they must
 * survive pass 1; an accepted outside-boundary annotation is the one exception,
 * because its human override bypasses scalar filters while overlap still runs.
 * The walk adds the fixed containment check on top of the slider's IoU stage.
 * Suppressions persist as the existing `overlapTooHigh` reason.
 */
export function computeFilterHiddenReasonsForList(
  annotations: Annotation[],
  bboxKeypointDistances: (number | undefined)[],
  thresholds: SegmentationFilterThresholds,
  containment?: { params: ContainmentParams; getMask: (index: number) => boolean[][] | null }
): HiddenReason[][] {
  const reasonsByIndex = annotations.map((annotation, index) =>
    isManualBBoxAnnotation(annotation)
      ? []
      : computePassOneReasons(annotation, bboxKeypointDistances[index], thresholds)
  );

  if (!containment) {
    // applyNMS only ever suppresses non-manual annotations, so !keep[index]
    // can never point at a manual one.
    const keep = applyNMS(annotations, thresholds.maxOverlapIou);
    keep.forEach((kept, index) => {
      if (!kept) reasonsByIndex[index].push("overlapTooHigh");
    });
    return reasonsByIndex;
  }

  // `typeof === "number"` matches the shared evaluate guard and iOS's nil-drop;
  // the two must agree, or a null-confidence box would pass candidacy here yet
  // be dropped by evaluate and wrongly force-marked overlapTooHigh.
  const candidateIndices = annotations
    .map((annotation, index) =>
      !isManualBBoxAnnotation(annotation) &&
      annotation.bbox &&
      typeof annotation.confidence === "number" &&
      (reasonsByIndex[index].length === 0 || isAcceptedOutsideBoundaryAnnotation(annotation)) &&
      !hasActivePreservedSystemReason(annotation) &&
      !hasUserToggledOff(annotation)
        ? index
        : -1
    )
    .filter((index) => index !== -1);

  const detections: OverlapDetection[] = candidateIndices.map((index) => ({
    bbox: annotations[index].bbox,
    confidence: annotations[index].confidence,
    // Resolved only for class-aware traits (open-set detectors) — the seven
    // fixed-model crops would discard it, and this runs per detection.
    classLabel: containment.params.classAware ? resolveDetectionClassLabel(annotations[index]) : undefined,
    getMask: () => containment.getMask(index),
  }));
  const { keep } = evaluateOverlapSuppression(detections, thresholds.maxOverlapIou, containment.params);
  candidateIndices.forEach((annotationIndex, candidateOrdinal) => {
    if (!keep[candidateOrdinal]) {
      reasonsByIndex[annotationIndex].push("overlapTooHigh");
    }
  });
  return reasonsByIndex;
}

/**
 * Non-Max Suppression: returns a boolean array where true = keep.
 * Annotations without bbox or confidence are always kept.
 * Among those with both fields: sort by confidence desc, greedily keep
 * each annotation and suppress any later one with IOU > threshold.
 */
export function applyNMS(annotations: Annotation[], iouThreshold: number): boolean[] {
  const keep = new Array(annotations.length).fill(true);
  const validIndices = annotations
    .map((a, i) => (a.bbox && a.segmentation && a.confidence !== undefined && !isManualBBoxAnnotation(a) ? i : -1))
    .filter((i) => i !== -1)
    .sort((i, j) => (annotations[j].confidence ?? 0) - (annotations[i].confidence ?? 0));

  const suppressed = new Set<number>();
  for (const i of validIndices) {
    if (suppressed.has(i)) {
      keep[i] = false;
      continue;
    }
    for (const j of validIndices) {
      if (j === i || suppressed.has(j)) continue;
      const iou = computeIoU(annotations[i].bbox!, annotations[j].bbox!);
      if (iou > iouThreshold) suppressed.add(j);
    }
  }
  return keep;
}
